import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${API_BASE_URL}/users`;

  constructor(private http: HttpClient) {}

  getRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/roles`);
  }

  getUsers(): Observable<any[]> {
    return this.http.get<any[]>(this.apiUrl);
  }

  getUsersByTenant(
    tenantId: number,
    search?: string,
    controlhelpOnly?: boolean,
    includeInactive?: boolean
  ): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', String(tenantId));
    if (search) {
      params = params.set('search', search);
    }
    if (controlhelpOnly) {
      params = params.set('controlhelp_only', '1');
    }
    if (includeInactive) {
      params = params.set('include_inactive', '1');
    }
    return this.http.get<any[]>(`${this.apiUrl}/by-tenant`, { params });
  }

  getUserModules(userId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${userId}/modules`);
  }

  getUserCadastrosPermissions(userId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${userId}/cadastros-permissions`);
  }

  getUserControlopAssignments(userId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${userId}/controlop-assignments`);
  }

  getUserControlhelpAssignments(userId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${userId}/controlhelp-assignments`);
  }

  checkEmailAvailability(email: string): Observable<{ exists: boolean; id?: number; name?: string; active?: number }> {
    return this.http.get<{ exists: boolean; id?: number; name?: string; active?: number }>(`${this.apiUrl}/check-email`, {
      params: { email }
    });
  }

  createUser(payload: any): Observable<any> {
    return this.http.post(this.apiUrl, payload);
  }

  updateUser(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, payload);
  }

  updateUserIpAccess(id: number, ipAccessExempt: boolean): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/ip-access`, { ip_access_exempt: ipAccessExempt ? 1 : 0 });
  }

  deleteUser(id: number, userId?: number): Observable<any> {
    const params = userId ? { user_id: String(userId) } : undefined;
    return this.http.delete(`${this.apiUrl}/${id}`, { params });
  }

  deactivateUser(id: number, userId?: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/deactivate`, { user_id: userId || null });
  }

  reactivateUser(id: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/reactivate`, {});
  }

  resendActivationEmail(id: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/${id}/resend`, {});
  }

  resetPassword(id: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/${id}/reset-password`, {});
  }

  requestActivation(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/activation/request`, { email });
  }

  validateActivationToken(email: string, token: string): Observable<{ sessionToken: string }> {
    return this.http.post<{ sessionToken: string }>(`${this.apiUrl}/activation/validate`, { email, token });
  }

  completeActivation(data: { email: string; token: string; password: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/activation/complete`, data);
  }

  getActivationInfo(email: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/activation/info`, { params: { email } });
  }

  uploadAvatar(id: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('avatar', file);
    return this.http.post(`${this.apiUrl}/${id}/avatar`, formData);
  }
}
